import React from "react";

const inlinePattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;

const renderInlineMarkdown = (text: string): React.ReactNode[] => {
    return text.split(inlinePattern).map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }

        if (part.startsWith("`") && part.endsWith("`")) {
            return (
                <code key={index} className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-700">
                    {part.slice(1, -1)}
                </code>
            );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
};

export const renderMarkdown = (markdown: string): React.ReactNode => {
    const lines = markdown.split(/\r?\n/);
    const nodes: React.ReactNode[] = [];
    let listItems: string[] = [];

    const flushList = () => {
        if (listItems.length === 0) return;
        nodes.push(
            <ul key={`list-${nodes.length}`} className="my-2 list-disc space-y-1 pl-5">
                {listItems.map((item, index) => (
                    <li key={index}>{renderInlineMarkdown(item)}</li>
                ))}
            </ul>
        );
        listItems = [];
    };

    lines.forEach((line, index) => {
        const trimmed = line.trim();

        if (!trimmed) {
            flushList();
            return;
        }

        if (trimmed.startsWith("- ")) {
            listItems.push(trimmed.slice(2));
            return;
        }

        flushList();

        const topMarginClass = nodes.length === 0 ? "mt-0" : "mt-3";

        if (trimmed.startsWith("### ")) {
            nodes.push(
                <h4 key={index} className={`${topMarginClass} text-sm font-semibold text-gray-900 dark:text-gray-100`}>
                    {renderInlineMarkdown(trimmed.slice(4))}
                </h4>
            );
            return;
        }

        if (trimmed.startsWith("## ")) {
            nodes.push(
                <h3 key={index} className={`${topMarginClass} text-base font-semibold text-gray-900 dark:text-gray-100`}>
                    {renderInlineMarkdown(trimmed.slice(3))}
                </h3>
            );
            return;
        }

        if (trimmed.startsWith("# ")) {
            nodes.push(
                <h2 key={index} className={`${topMarginClass} text-lg font-semibold text-gray-900 dark:text-gray-100`}>
                    {renderInlineMarkdown(trimmed.slice(2))}
                </h2>
            );
            return;
        }

        nodes.push(
            <p key={index} className={nodes.length === 0 ? "mb-2 mt-0" : "my-2"}>
                {renderInlineMarkdown(trimmed)}
            </p>
        );
    });

    flushList();

    return nodes;
};
